import Paginate from "@/components/Paginate";
import { useAdContext } from "@/contexts/AdContext";
import { useHeaderContext } from "@/contexts/HeaderContext";
import getCommonStaticProps from "@/lib/getCommonStaticProps";
import createBreadcrumbsJsonLd from "@/src/recipes/lib/createBreadcrumbsJsonLd";
import EntryGrid from "@components/EntryGrid";
import SplitViewLayout from "@components/SplitViewLayout";
import clsx from "classnames";
import craft from "lib/craft";
import Head from "next/head";
import QueryMealPlanCollections from "queries/QueryMealPlanCollections.graphql";
import QueryMealPlanCollectionsIndex from "queries/QueryMealPlanCollectionsIndex.graphql";
import { useEffect } from "react";
import { Seomatic } from "react-seomatic";

const limit = 30;

export async function getStaticProps({ params, previewData, preview }) {
  // Fetch common static properties
  const commonStaticProps = await getCommonStaticProps();

  const queries = [
    {
      document: QueryMealPlanCollectionsIndex,
    },
    {
      document: QueryMealPlanCollections,
      variables: {
        limit,
        offset: 0,
      },
    },
  ];

  const response = await craft(queries, preview ? previewData.token : null);

  const { pageEntry, collectionsCount } = response[0];
  const { mealPlanCollections } = response[1];

  const breadcrumbs = [
    { label: "Meal Plans", href: "/meal-plans" },
    { label: "Meal Plan Collections", href: "/meal-plans/collections" },
  ];

  const jsonLdSchema = [createBreadcrumbsJsonLd(breadcrumbs)];

  return {
    props: {
      ...commonStaticProps,
      pageEntry,
      seomatic: pageEntry.seomatic,
      collectionsCount,
      mealPlanCollections,
      breadcrumbs,
      jsonLdSchema,
    },
    revalidate: process.env.VERCEL_ENV !== "production" ? 1 : 3600,
  };
}

const MealPlanCollectionsIndex = ({
  pageEntry,
  mealPlanCollections,
  collectionsCount,
}) => {
  // Set showAds to true for this page
  const { setShowAds } = useAdContext();
  useEffect(() => setShowAds(true));

  // Show the header border for this page
  const { setShowBorder } = useHeaderContext();
  useEffect(() => {
    setShowBorder(true);
    return () => setShowBorder(false);
  }, []);

  const pageCount = Math.floor(collectionsCount / limit);

  // For the header, use either the index page's image, or the last collection's
  const images =
    pageEntry.image && pageEntry.image.length > 0
      ? pageEntry.image
      : mealPlanCollections?.[mealPlanCollections.length - 1]?.image ?? [];

  return (
    <>
      <Seomatic Head={Head} {...pageEntry.seomatic} />

      <div className={clsx("border-b")}>
        <SplitViewLayout
          heading={pageEntry.title}
          introduction={
            pageEntry.summary ||
            "Get inspired. Your favourite meal plans, conveniently collected"
          }
          images={images}
        />
      </div>

      <div className={clsx("border-b px-6 py-6 lg:px-12 lg:py-12")}>
        <EntryGrid entries={mealPlanCollections} />
      </div>

      {pageCount > 1 && (
        <div className={clsx("border-t")}>
          <Paginate
            pageCount={pageCount}
            activePage={1}
            baseUri="/meal-plans/collections"
          />
        </div>
      )}
    </>
  );
};

export default MealPlanCollectionsIndex;
